import * as React from 'react';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Typography, Tooltip, IconButton } from '@mui/material';

import { CurrencyFormat } from '@ha/intl';

import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { useIntl } from '@kamernet/core/Intl';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { CommonStyles } from '@kamernet/styles/CommonStyles';
import { getFormattedDate } from '@kamernet/utilities/DateTime/getFormattedDate';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import { SignupCard } from '@kamernet/modules/Listing/components/SignupCard';
import { useFormatDateRange } from '@kamernet/modules/Listing/hooks';
import {
  FurnishingLabelTranslations,
  RoomTypeLabelTranslations,
} from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';

import styles from './PropertyDetails.module.css';

export const PropertyDetails = () => {
  const { listingDetails } = ListingDetailsContext.useContext();
  const { isAuthenticated, userProfile } = AuthContext.useContext();
  const { T, formatCurrency } = useIntl();

  const isMobile = !useMediaIsDesktop();
  const rentalPrice = listingDetails.totalRentalPrice
    ? listingDetails.totalRentalPrice * 100
    : 0;

  const formatDateRange = useFormatDateRange();

  const availableFromDate = listingDetails.availabilityStartDate
    ? formatDateRange(listingDetails.availabilityStartDate).toLocaleLowerCase()
    : '';

  const availableEndDate = listingDetails.availabilityEndDate
    ? T(
        '%_%',
        T('LBL_UNTIL'),
        getFormattedDate(
          listingDetails.availabilityEndDate,
          'YYYY-MM-DDTHH:mm:ss',
        ).toLocaleLowerCase(),
      )
    : T('ldp.rental_period.indefinite');

  const furnishing = listingDetails.furnishingId
    ? T(FurnishingLabelTranslations[listingDetails.furnishingId])
    : '';

  const listingType = listingDetails.listingTypeId
    ? T(
        RoomTypeLabelTranslations[listingDetails.listingTypeId],
      ).toLocaleLowerCase()
    : '';
  const viewingPossibleDate = listingDetails.viewingDate
    ? getFormattedDate(
        listingDetails.viewingDate.toString(),
        'YYYY-MM-DDTHH:mm:ss',
      ).toLocaleLowerCase()
    : T('ldp.ask_lld_about_viewing');

  return (
    <section>
      {isMobile && (
        <Typography className={CommonStyles.margin_bottom_2} variant="h5">
          {T('ldp.overview.header')}
        </Typography>
      )}
      {!isMobile && (
        <div className={styles.row}>
          <div className={styles.icon}>
            <PaymentsOutlinedIcon />
          </div>

          <NavLink.Native href="#cost-breakups" underline="none">
            <div className={styles.price}>
              <Typography variant="subtitle2">
                {formatCurrency(
                  rentalPrice,
                  'EUR',
                  CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION,
                )}
              </Typography>
              <Typography variant="body3">{T('label.per_month')}</Typography>
              <Tooltip
                color="secondary"
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: T('ldp.what_you_pay.cost_breakdown'),
                    }}
                  />
                }
              >
                <IconButton>
                  <InfoOutlinedIcon className={styles.infoButton} />
                </IconButton>
              </Tooltip>
            </div>

            <Typography variant="body4">
              {listingDetails.utilitiesIncluded
                ? T('ldp.incl_utilities')
                : T('ldp.excl_utilities')}
            </Typography>
          </NavLink.Native>
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.icon}>
          <StraightenIcon />
        </div>

        <div>
          <Typography variant="subtitle2">
            {T('ldp.surface_area', listingDetails.surfaceArea)}
          </Typography>
          <Typography variant="body4">
            {T('%_%', furnishing, listingType)}
          </Typography>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.icon}>
          <CalendarTodayIcon />
        </div>

        <div>
          <Typography variant="subtitle2">
            {T('ldp.available_from_date', availableFromDate)}
          </Typography>
          <Typography variant="body4">{availableEndDate}</Typography>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.icon}>
          <VisibilityOutlinedIcon />
        </div>

        <div>
          <Typography variant="subtitle2">
            {T('ldp.viewing_possible')}
          </Typography>
          <Typography variant="body4">
            {viewingPossibleDate &&
            isAuthenticated &&
            userProfile?.haspremiumaccount
              ? viewingPossibleDate
              : T('ldp.ask_lld_about_viewing')}
          </Typography>
        </div>
      </div>
      {isMobile && !isAuthenticated && (
        <div className={CommonStyles.margin_top_1}>
          <SignupCard
            hideImage
            title={T('search_alerts.ldp.heading.similar_places')}
            subtitle={T('search_alerts.ldp.body.like_this_place')}
            buttonMessage={T('search_alerts.ldp.cta.sign_up')}
          />
        </div>
      )}
    </section>
  );
};
